/**
 * Web Vitals monitoring - E19-S10
 *
 * Reports Core Web Vitals (CLS, LCP, FID, INP, TTFB) to the API
 * and optionally to the console in development.
 *
 * Uses the `web-vitals` library with attribution build for
 * diagnostic metadata (element, URL, interaction target, etc.).
 */

import type { Metric } from 'web-vitals'

/* ───── Types ───── */

interface VitalsPayload {
  name: string
  value: number
  rating: string
  delta: number
  id: string
  navigationType: string
  url: string
  timestamp: number
}

/* ───── Config ───── */

/** Batch size before flushing to the API */
const BATCH_SIZE = 10

/** Max time (ms) to wait before flushing even with fewer than BATCH_SIZE entries */
const FLUSH_INTERVAL = 30_000

/** API endpoint for vitals reporting */
const VITALS_ENDPOINT = '/api/v1/telemetry/vitals'

/* ───── Buffer ───── */

let buffer: VitalsPayload[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

/* ───── Flush Logic ───── */

function flush() {
  if (buffer.length === 0) return

  const payload = [...buffer]
  buffer = []

  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  // Use sendBeacon for reliability during page unload; fall back to fetch
  const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
  const sent = navigator.sendBeacon?.(VITALS_ENDPOINT, blob)

  if (!sent) {
    fetch(VITALS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
      keepalive: true,
    }).catch(() => {
      // Silently drop - vitals are non-critical telemetry
    })
  }
}

function scheduleFlush() {
  if (flushTimer) return
  flushTimer = setTimeout(flush, FLUSH_INTERVAL)
}

/* ───── Metric Handler ───── */

function handleMetric(metric: Metric) {
  const entry: VitalsPayload = {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
    url: window.location.href,
    timestamp: Date.now(),
  }

  // Log in development for debugging
  if (import.meta.env.DEV) {
    const color =
      metric.rating === 'good' ? '#0cce6b' :
      metric.rating === 'needs-improvement' ? '#ffa400' :
      '#ff4e42'
    // eslint-disable-next-line no-console
    console.log(
      `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(1)} (${metric.rating})`,
      `color: ${color}; font-weight: bold;`,
    )
  }

  buffer.push(entry)

  if (buffer.length >= BATCH_SIZE) {
    flush()
  } else {
    scheduleFlush()
  }
}

/* ───── Public API ───── */

/**
 * Initializes web vitals reporting. Should be called once at app startup.
 * Dynamically imports `web-vitals` so the library is tree-shaken
 * and loaded after the critical rendering path.
 */
export async function initWebVitals(): Promise<void> {
  try {
    const { onCLS, onLCP, onINP, onTTFB } = await import('web-vitals')

    onCLS(handleMetric)
    onLCP(handleMetric)
    onINP(handleMetric)
    onTTFB(handleMetric)

    // Flush remaining buffer on page unload
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flush()
      }
    })
  } catch {
    // web-vitals import failed - non-critical, silently degrade
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[Web Vitals] Failed to load web-vitals library')
    }
  }
}
